import type { FederatedPointerEvent } from "pixi.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMapPointerPosition } from "../getMapPointerPosition";
import type { InteractionTarget } from "../managers/InteractionManager";
import type {
    CorvusMapObject,
    LayerId,
    MapItemHandler,
    MapPointerEvent,
} from "../types";
import { useScene } from "./useScene";

interface Options<TItem extends CorvusMapObject> {
    item: TItem;
    layerId: LayerId;
    onItemClick?: MapItemHandler<TItem>;
    onItemHover?: MapItemHandler<TItem>;
    onItemLeave?: MapItemHandler<TItem>;
}

export function useMapObjectInteractions<TItem extends CorvusMapObject>({
    item,
    layerId,
    onItemClick,
    onItemHover,
    onItemLeave,
}: Options<TItem>) {
    const { coordinates, interactionManager, viewport } = useScene();
    const [hovered, setHovered] = useState(false);

    const target = useRef<InteractionTarget>({}).current;
    const latestOnItemHoverRef = useRef(onItemHover);
    const latestOnItemLeaveRef = useRef(onItemLeave);

    latestOnItemHoverRef.current = onItemHover;
    latestOnItemLeaveRef.current = onItemLeave;

    const eventMode =
        item.interactive === false ? ("none" as const) : ("static" as const);
    const cursor =
        item.interactive === false
            ? undefined
            : onItemClick
              ? "pointer"
              : undefined;

    const createMapEvent = useCallback(
        (
            originalEvent: FederatedPointerEvent,
        ): MapPointerEvent<TItem> | null => {
            const pointerPosition = getMapPointerPosition(
                originalEvent,
                viewport,
                coordinates,
            );
            if (!pointerPosition) return null;

            return {
                item,
                objectRef: { layerId, id: item.id },
                ...pointerPosition,
                ctrlKey: originalEvent.ctrlKey,
                metaKey: originalEvent.metaKey,
            };
        },
        [coordinates, item, layerId, viewport],
    );

    useEffect(() => {
        if (item.interactive === false) {
            interactionManager.removeTarget(target);
        }

        return () => interactionManager.removeTarget(target);
    }, [interactionManager, item.interactive, target]);

    const handlePointerOver = useCallback(
        (event: FederatedPointerEvent) => {
            const mapEvent = createMapEvent(event);
            if (!mapEvent) return;

            target.onHoverChange = (hovered) => {
                setHovered(hovered);

                if (hovered) {
                    latestOnItemHoverRef.current?.(mapEvent);
                } else {
                    latestOnItemLeaveRef.current?.(mapEvent);
                }
            };

            interactionManager.pointerOver(event, target);
        },
        [createMapEvent, interactionManager, target],
    );

    const handlePointerOut = useCallback(
        (event: FederatedPointerEvent) => {
            interactionManager.pointerOut(event, target);
        },
        [interactionManager, target],
    );

    const handlePointerTap = useCallback(
        (event: FederatedPointerEvent) => {
            const mapEvent = createMapEvent(event);

            target.onClick =
                mapEvent && onItemClick
                    ? () => onItemClick(mapEvent)
                    : undefined;

            interactionManager.pointerTap(event, target);
        },
        [createMapEvent, interactionManager, onItemClick, target],
    );

    return useMemo(
        () => ({
            hovered,
            eventMode,
            cursor,
            onPointerOver: handlePointerOver,
            onPointerOut: handlePointerOut,
            onPointerTap: handlePointerTap,
        }),
        [
            hovered,
            eventMode,
            cursor,
            handlePointerOver,
            handlePointerOut,
            handlePointerTap,
        ],
    );
}
